import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../_environments/environment';
import { WINDOW } from './WindowService';

const SERVER_SIDE_EVENTS = new Set(['purchase', 'purchase_complete', 'sign_up']);
const BUFFER_INTERVAL_MS = 250;
const BUFFER_MAX_ATTEMPTS = 120; // 30 seconds

@Injectable({ providedIn: 'root' })
export class CntrcService {

  private buffer: any[] = [];
  private intervalId: any = null;
  private attempts = 0;
  private lastPagePath: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(WINDOW) private window: Window,
  ) {}

  init(): void {
    if (!environment.cntrcUrl) return;
    if (!isPlatformBrowser(this.platformId)) return;

    const dataLayer = (this.window as any)['dataLayer'] ||= [];
    const originalPush = dataLayer.push.bind(dataLayer);
    dataLayer.push = (...events: any[]) => {
      for (const evt of events) {
        try { this.mirror(evt); } catch (e) { console.warn('cntrc mirror failed', e); }
      }
      return originalPush(...events);
    };
  }

  mirror(evt: any): void {
    if (evt.event === 'virtualPageview') { this.sendPageView(); return; }
    if (evt.event === 'button_click') { this.sendButtonClick(evt.label); return; }
    if (!evt.ecommerce) return;
    if (SERVER_SIDE_EVENTS.has(evt.event)) return;

    if (!(this.window as any)['cntrc']?.trackingId) {
      this.buffer.push(evt);
      this.startFlushInterval();
      return;
    }

    this.send(evt);
  }

  private startFlushInterval(): void {
    if (this.intervalId !== null) return;
    this.intervalId = setInterval(() => {
      this.attempts++;
      if ((this.window as any)['cntrc']?.trackingId) {
        this.flushBuffer();
        this.clearFlushInterval();
      } else if (this.attempts >= BUFFER_MAX_ATTEMPTS) {
        this.buffer = [];
        this.clearFlushInterval();
      }
    }, BUFFER_INTERVAL_MS);
  }

  private flushBuffer(): void {
    const events = [...this.buffer];
    this.buffer = [];
    for (const evt of events) {
      this.send(evt);
    }
  }

  private clearFlushInterval(): void {
    clearInterval(this.intervalId);
    this.intervalId = null;
    this.attempts = 0;
  }

  private sendPageView(): void {
    const cntrc = (this.window as any)['cntrc'];
    if (!cntrc?.sendEvent) return;
    const path = this.window.location.pathname;
    if (path === this.lastPagePath) return;
    this.lastPagePath = path;
    cntrc.sendEvent({ event: 'page_visit' });
  }

  private sendButtonClick(label: string | undefined): void {
    if (!label) return;
    const cntrc = (this.window as any)['cntrc'];
    if (!cntrc?.trackingId) return;
    fetch(`${environment.cntrcUrl}/api/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'button_click',
        trackingId: cntrc.trackingId,
        fingerprint: cntrc.fingerprint,
        path: this.window.location.pathname,
        pageTitle: this.window.document.title,
        metaData: { buttonLabel: label },
      }),
      keepalive: true,
    }).catch(e => console.warn('cntrc post failed', e));
  }

  private send(evt: any): void {
    const cntrc = (this.window as any)['cntrc'];
    const items = (evt.ecommerce.items || [])
      .map((it: any) => ({ id: it.item_id ?? it.id ?? it.sku, quantity: it.quantity ?? 1 }))
      .filter((it: any) => it.id != null);
    if (items.length === 0) return;
    fetch(`${environment.cntrcUrl}/api/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: evt.event,
        trackingId: cntrc.trackingId,
        fingerprint: cntrc.fingerprint,
        path: this.window.location.pathname,
        pageTitle: this.window.document.title,
        ecommerceData: {
          currency: evt.ecommerce.currency || 'EUR',
          value: evt.ecommerce.value ?? 0,
          items,
          ...(evt.ecommerce.shipping_tier && { shippingType: evt.ecommerce.shipping_tier }),
          ...(evt.ecommerce.payment_type && { paymentType: evt.ecommerce.payment_type }),
        },
      }),
      keepalive: true,
    }).catch(e => console.warn('cntrc post failed', e));
  }
}
